import { PayloadAction } from '@reduxjs/toolkit';
import createAsyncSlice from '../createAsyncSlice';
import { Answer, Question, VoteData } from '../../types';
import { getQuestionById } from '../../services/questionService';

/**
 * Represents the state of the answer page.
 */
export interface AnswersSliceState {
  question: Question | undefined;
  questionID: string;
  status: 'idle' | 'loading' | 'failed';
}

/**
 * Represents the initial state of the answer page.
 */
const initialState: AnswersSliceState = {
  question: undefined,
  questionID: '',
  status: 'idle',
};

/**
 * Creates reducers and selectors with which to used to interact with the answer page state.
 */
export const answersSlice = createAsyncSlice({
  name: 'answerPage',
  initialState,
  reducers: create => ({
    // Updates the state by fetching a question from the database using the given ID and username
    fetchQuestion: create.asyncThunk(
      async (answerData: { questionID: string; username: string }) => {
        const res = await getQuestionById(answerData.questionID, answerData.username);
        return res || [];
      },
      {
        pending: state => {
          state.status = 'loading';
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.question = action.payload;
        },
        rejected: state => {
          state.status = 'failed';
        },
      },
    ),
    // Updates the state's question with the given question if the ID is correct
    updateAPQuestion: create.reducer((state, action: PayloadAction<Question>) => {
      if (action.payload._id === state.questionID) state.question = action.payload;
    }),
    // Updates the state's question by appending given answer if the question exists
    updateAPAnswer: create.reducer((state, action: PayloadAction<Answer>) => {
      state.question = state.question
        ? {
            ...state.question,
            answers: state.question.answers.map(a =>
              a._id === action.payload._id ? action.payload : a,
            ),
          }
        : state.question;
    }),
    // Adds the given answer to the state's question if the ID is correct and the question exists
    addAnswer: create.reducer((state, action: PayloadAction<{ id: string; answer: Answer }>) => {
      if (action.payload.id === state.questionID)
        state.question = state.question
          ? { ...state.question, answers: [...state.question.answers, action.payload.answer] }
          : state.question;
    }),
    // Updates the state's question with the given vote data and the question exists
    updateVotes: create.reducer((state, action: PayloadAction<VoteData>) => {
      if (action.payload.qid === state.questionID)
        state.question = state.question
          ? {
              ...state.question,
              upVotes: [...action.payload.upVotes],
              downVotes: [...action.payload.downVotes],
            }
          : state.question;
    }),
    // Sets the state's question ID
    setQuestionID: create.reducer((state, action: PayloadAction<string>) => {
      state.questionID = action.payload;
    }),
  }),
  selectors: {
    questionSelector: state => state.question,
    questionIDSelector: state => state.questionID,
    statusSelector: state => state.status,
  },
});

export const {
  fetchQuestion,
  updateAPQuestion,
  updateAPAnswer,
  addAnswer,
  updateVotes,
  setQuestionID,
} = answersSlice.actions;

export const { questionSelector, questionIDSelector, statusSelector } = answersSlice.selectors;
